import { ccsOptionSets } from '@chiroup/core/constants/CCSOptionSets';
import {
  CCSBodyPickerQuestionIds,
  CCSPrimaryQuestionIds,
  CCSSecondaryQuestionIds,
  CCSSpecialQuestionIds,
  CCSSpecialQuestionTypes,
  abbreviatedCCSQuestionsIds,
  customCCSMoreComplaintsQuestionId,
} from '@chiroup/core/constants/CCSQuestionIds';
import { surveyIds as theSurveyIds } from '@chiroup/core/constants/SurveyIds';
import { Genders } from '@chiroup/core/enums/Genders.enum';
import { getAggravatesForBodyParts } from '@chiroup/core/functions/aggravatesForBodyParts';
import { createName } from '@chiroup/core/functions/createName';
import { deriveCCSFollowupsFromBodyParts } from '@chiroup/core/functions/deriveCCSFollowups';
import { normalizeCCSSleepConditions } from '@chiroup/core/functions/normalizeCCSSleepConditions';
import { replaceComplaintMergeField } from '@chiroup/core/functions/replaceComplaintMergeField';
import { RequestStep } from '@chiroup/core/types/Request.type';
import {
  BodyParts,
  Part,
  SourceOfPain,
  SurveyQuestion,
  SurveyQuestionIfType,
} from '@chiroup/core/types/Survey.type';
import {
  createAbbreviatedChiefComplaintQuestions,
  createChiefComplaintQuestions,
} from './ccsQuestions';

export const ccsBodyPickerQuestion = {
  id: 'e89903d4-5f69-4c64-a3fc-7827c9179514',
  createdAt: 1609451279109,
  question: 'Please click on the body to select the parts that are causing you pain.',
  type: 'bodypicker',
  updatedAt: 1609451279109,
};

export const ccsBodyPickerQuestionSpanish = {
  id: 'e89903d4-5f69-4c64-a3fc-7827c9179514',
  createdAt: 1609451279109,
  question:
    'En la siguiente pantalla, haga clic en el cuerpo para seleccionar las partes que le causan dolor.',
  type: 'bodypicker',
  updatedAt: 1609451279109,
};

const addChiefComplaintQuestions = (
  parts: BodyParts,
  language: string,
  currentQuestion: SurveyQuestion,
  questions: SurveyQuestion[],
  spanishQuestions: SurveyQuestion[],
  setCurrentQuestion: (val: SurveyQuestion) => void,
  setQuestions: (val: SurveyQuestion[]) => void,
  setSpanishQuestions: (val: SurveyQuestion[]) => void,
  isProvider = false,
  additionalComplaint = false,
  aaWI?: string,
  startingPoblemArea = 0,
  abbreviatedCCS = false,
  allowAdditionalComplaints = true,
  age?: number,
  gender?: Genders
) => {
  const currentQuestionId = currentQuestion?.id;
  const lastQuestionOrder = questions?.length - 1;
  const newEnglishQuestions = abbreviatedCCS
    ? questions.concat(
        createAbbreviatedChiefComplaintQuestions(parts, 'en', allowAdditionalComplaints)
      )
    : questions.concat(
        createChiefComplaintQuestions(
          parts,
          lastQuestionOrder,
          currentQuestionId === CCSBodyPickerQuestionIds.primary
            ? CCSPrimaryQuestionIds
            : CCSSecondaryQuestionIds,
          'en',
          currentQuestionId === CCSBodyPickerQuestionIds.primary ? 1 : 2,
          isProvider,
          aaWI,
          startingPoblemArea,
          additionalComplaint
        )
      );

  const newSpanishQuestions =
    spanishQuestions && abbreviatedCCS
      ? spanishQuestions.concat(
          createAbbreviatedChiefComplaintQuestions(parts, 'es', allowAdditionalComplaints)
        )
      : spanishQuestions
        ? spanishQuestions.concat(
            createChiefComplaintQuestions(
              parts,
              lastQuestionOrder,
              currentQuestionId === CCSBodyPickerQuestionIds.primary
                ? CCSPrimaryQuestionIds
                : CCSSecondaryQuestionIds,
              'es',
              currentQuestionId === CCSBodyPickerQuestionIds.primary ? 1 : 2,
              isProvider,
              aaWI,
              startingPoblemArea,
              additionalComplaint
            )
          )
        : [];

  const nextQuestionIndex = getNextQuestionIndex({
    questions: newEnglishQuestions,
    age,
    gender,
  });
  const nextQuestion = ((language === 'es' ? newSpanishQuestions : newEnglishQuestions) || [])?.[
    nextQuestionIndex
  ];
  setCurrentQuestion(nextQuestion);
  setQuestions(newEnglishQuestions || []);

  if (setSpanishQuestions && language === 'es') {
    setSpanishQuestions(newSpanishQuestions || []);
  }
};

/**
 * Returns true when the selected body parts include a region affected on
 * both left AND right sides — the condition that triggers the "Is the
 * intensity of your symptoms:" bilateral question in the built-in CCS.
 */
const hasBilateralIssue = (parts: BodyParts): boolean => {
  const bothSides = (region: Part) =>
    !!(parts.front[region]?.left || parts.rear[region]?.left) &&
    !!(parts.front[region]?.right || parts.rear[region]?.right);

  return (
    bothSides('neck') ||
    bothSides('upper-back') ||
    bothSides('lower-back') ||
    bothSides('upper-lower-back') ||
    bothSides('head') ||
    bothSides('chest') ||
    bothSides('abdomen') ||
    bothSides('groin')
  );
};

const specialQuestionTypeSet = new Set<string>(CCSSpecialQuestionTypes);

// Also detect provocative/palliative by question ID, not just type.
// When a CCS is imported from the library, the aggravates & relief questions
// arrive as type: 'checkbox' with ALL options pre-populated. We need to
// re-expand them using the body-part-filtered option set.
const specialQuestionIdMap: Record<string, 'provocative' | 'palliative'> = {
  [CCSSpecialQuestionIds.provocative]: 'provocative',
  [CCSSpecialQuestionIds.palliative]: 'palliative',
};

const toOptions = (opts: { en: string; es: string }[], language: string) =>
  opts.map(o => ({
    display: language === 'es' ? o.es : o.en,
    id: o.en,
  }));

export const isSpecialQuestion = (q: SurveyQuestion): 'provocative' | 'palliative' | undefined => {
  if (specialQuestionTypeSet.has(q.type)) return q.type as 'provocative' | 'palliative';

  return specialQuestionIdMap[q.id];
};

export const expandSpecialQuestion = (
  q: SurveyQuestion,
  parts: BodyParts,
  regionName: string,
  language: string,
  problemArea: number,
  specialType: 'provocative' | 'palliative'
): SurveyQuestion => {
  if (specialType === 'provocative') {
    const aggravateOptions = getAggravatesForBodyParts(parts);

    // typeOptions is intentionally NOT set here — standard generators
    // (interimSurveyHelpers.ts createProvocativeQuestionDirect and
    // ccsQuestions.ts createAggravatesQuestion) do not include it. Templates
    // that need `includeOther: true` carry it themselves via the `...q` spread.
    return {
      ...q,
      id: q.id,
      question:
        language === 'es'
          ? `¿Qué empeora sus síntomas en ${regionName}?`
          : `What makes your ${regionName} symptoms worse?`,
      type: 'checkbox',
      problemArea,
      options: toOptions(aggravateOptions, language),
    };
  }

  if (specialType === 'palliative') {
    // typeOptions is intentionally NOT set here — see provocative comment above.
    // The reeval relief template carries `typeOptions: { includeOther: true }`
    // itself; the interim palliative template does not (matching its built-in).
    return {
      ...q,
      id: q.id,
      question:
        language === 'es'
          ? `¿Hay algo que le haya ayudado a aliviar sus síntomas en ${regionName}?`
          : `Does/has anything helped relieve your ${regionName} symptoms?`,
      type: 'checkbox',
      problemArea,
      options: toOptions(ccsOptionSets.relief, language),
    };
  }

  return q;
};

/**
 * Strip template-only metadata so the expanded custom-path output matches
 * the structural shape the standard generator (createAbbreviatedChiefComplaintQuestions
 * / createChiefComplaintQuestions) produces. Without this strip, custom-path
 * questions leak `templateAlias` into the patient-facing survey payload.
 */
const stripCCSTemplateMetadata = (q: SurveyQuestion): SurveyQuestion => {
  const { templateAlias, showOnVisit, ...rest } = q as any;
  void templateAlias;
  void showOnVisit;

  return rest;
};

/**
 * The standard CCS "describe" question (ccsQuestions.ts:117-121) appends a
 * Dizziness option when the complaint region includes 'head'. Mirror that
 * here so custom-imported templates render the same option set for head
 * complaints. Routes on stable question IDs (mainQuestionId) rather than
 * user-editable templateAlias.
 */
const augmentDescribeOptionsForRegion = (
  q: SurveyQuestion,
  regionName: string,
  language: string
): SurveyQuestion => {
  const isDescribeQuestion =
    q.id === CCSPrimaryQuestionIds.mainQuestionId ||
    q.id === CCSSecondaryQuestionIds.mainQuestionId;
  if (!isDescribeQuestion) return q;
  if (!regionName?.toLowerCase().includes('head')) return q;
  const dizziness = {
    id: 'Dizziness',
    display: language === 'es' ? 'Convulsiones Mareo' : 'Dizziness',
  };
  const opts = (q.options || []).filter(o => o?.id !== 'Dizziness');

  return { ...q, options: [...opts, dizziness] };
};

/**
 * Build per-complaint CCS / abbreviated-CCS questions from a custom survey
 * template. Pure function, extracted from addCustomCCSQuestions so parity
 * tests and the preview modal can call it without mounting the component.
 *
 * - Filters bodypicker and non-applicable bilateral questions.
 * - Replaces `{complaint}` merge fields with the computed region name.
 * - Expands provocative/palliative specials to body-part-specific option sets.
 * - Augments the describe question's options with Dizziness for head regions.
 * - Optionally appends the customCCSMoreComplaints trailing question.
 */
// fallow-ignore-next-line unused-export
export const buildCustomCCSQuestions = (
  customQuestions: SurveyQuestion[],
  parts: BodyParts,
  problemArea: number,
  language: string,
  allowAdditionalComplaints = false,
  applyAbbreviatedParityFixes = false
): SurveyQuestion[] => {
  const regionName = createName(parts);

  // Skip the bilateral question when the selected body parts don't have
  // issues on both sides (matching the built-in CCS behaviour).
  const isBilateral = hasBilateralIssue(parts);
  const templateQuestions = customQuestions.filter(q => {
    if (q.type === 'bodypicker') return false;
    if (q.id === CCSPrimaryQuestionIds.bilateralQuestionId && !isBilateral) return false;
    // Abbreviated templates bake in the "more symptoms" question. Strip it
    // when the clinic has disabled the additional-complaints toggle so the
    // survey ends on the severity slider.
    if (
      applyAbbreviatedParityFixes &&
      !allowAdditionalComplaints &&
      q.id === abbreviatedCCSQuestionsIds.moreComplaintsQuestionId
    ) {
      return false;
    }
    return true;
  });

  const mergedQuestions: SurveyQuestion[] = applyAbbreviatedParityFixes
    ? // Replace merge fields, set problemArea, and expand special question types.
      // Detection uses both question type AND question ID — questions imported from
      // the CCS library arrive as type: 'checkbox' but must still be expanded.
      // Global questions (template problemArea === 0) remain global; their
      // problemArea is stripped so output matches the standard generator, which
      // omits the field for global questions.
      templateQuestions.map(q => {
        const isGlobal = q.problemArea === 0;
        const specialType = isSpecialQuestion(q);

        if (specialType) {
          return stripCCSTemplateMetadata(
            expandSpecialQuestion(
              q,
              parts,
              regionName,
              language,
              isGlobal ? (undefined as unknown as number) : problemArea,
              specialType
            )
          );
        }

        const augmented = augmentDescribeOptionsForRegion(q, regionName, language);

        const out: SurveyQuestion = stripCCSTemplateMetadata({
          ...augmented,
          question: augmented.question
            ? replaceComplaintMergeField(augmented.question, regionName)
            : augmented.question,
        });

        if (isGlobal) {
          delete (out as any).problemArea;
        } else {
          out.problemArea = problemArea;
        }

        return out;
      })
    : templateQuestions.map(q => {
        const specialType = isSpecialQuestion(q);

        if (specialType) {
          return expandSpecialQuestion(q, parts, regionName, language, problemArea, specialType);
        }

        return {
          ...q,
          question: q.question ? replaceComplaintMergeField(q.question, regionName) : q.question,
          problemArea,
          if: normalizeCCSSleepConditions(q.if),
          ifSome: normalizeCCSSleepConditions(q.ifSome),
        };
      });

  // Abbreviated templates already carry their own "more symptoms" question
  // (abbreviatedCCSQuestionsIds.moreComplaintsQuestionId) — appending the
  // custom one here would render two near-identical questions back-to-back.
  if (allowAdditionalComplaints && !applyAbbreviatedParityFixes) {
    mergedQuestions.push({
      id: customCCSMoreComplaintsQuestionId,
      question:
        language === 'es'
          ? '¿Tiene algún otro síntoma que le gustaría informar?'
          : 'Do you have any other complaints you would like to report?',
      type: 'radio',
      order: mergedQuestions.length + 1,
      problemArea,
      options: [
        { id: 'Yes', display: language === 'es' ? 'Sí' : 'Yes', score: 0 },
        { id: 'No', display: 'No', score: 0 },
      ],
    });
  }

  return mergedQuestions;
};

const addCustomCCSQuestions = (
  parts: BodyParts,
  language: string,
  currentQuestion: SurveyQuestion,
  questions: SurveyQuestion[],
  spanishQuestions: SurveyQuestion[],
  customQuestions: SurveyQuestion[],
  setCurrentQuestion: (val: SurveyQuestion) => void,
  setQuestions: (val: SurveyQuestion[]) => void,
  setSpanishQuestions: (val: SurveyQuestion[]) => void,
  _startingProblemArea = 0,
  allowAdditionalComplaints = false,
  abbreviatedCCS = false,
  age?: number,
  gender?: Genders
) => {
  const problemArea = currentQuestion.problemArea ?? 1;
  const mergedQuestions = buildCustomCCSQuestions(
    customQuestions,
    parts,
    problemArea,
    language,
    allowAdditionalComplaints,
    abbreviatedCCS
  );

  const newQuestions = questions.concat(mergedQuestions);

  const nextQuestionIndex = getNextQuestionIndex({ questions: newQuestions, age, gender });
  const nextQuestion = ((language === 'es'
    ? spanishQuestions.concat(mergedQuestions)
    : newQuestions) || [])?.[nextQuestionIndex];

  setCurrentQuestion(nextQuestion);
  setQuestions(newQuestions);

  if (setSpanishQuestions && language === 'es') {
    setSpanishQuestions(spanishQuestions.concat(mergedQuestions));
  }
};

export const submitStuff = async ({
  questions,
  kiosk,
  spanishQuestions,
  setIsSubmitting,
  isSubmitting,
  submitSurvey,
  complete,
  markSurveyComplete,
  onAccsChainExtend,
  setSubmitText,
  submitVerbiage,
  validationCode,
  language = 'en',
  surveyIds,
  ccsFollowups,
  customCCSSurveyId,
  currentSurveyId,
  updateRequest,
  complaints,
}: {
  questions: SurveyQuestion[];
  kiosk: boolean;
  spanishQuestions: SurveyQuestion[];
  setIsSubmitting: (val: boolean) => void;
  isSubmitting: boolean;
  submitSurvey: (
    questions: SurveyQuestion[],
    kiosk: boolean,
    spanishQuestions?: SurveyQuestion[],
    ccsFollowupSurveyIds?: string[],
    validationCode?: string | number
  ) => Promise<any>;
  complete: () => void;
  markSurveyComplete: () => void;
  // Fired instead of markSurveyComplete when an ACCS chain extends. The Survey
  // Complete "Thank you" screen is skipped — patient goes straight from the
  // ACCS Yes answer to the next ACCS's Get Started screen. For ACCS chain end
  // (No / cap) and non-ACCS surveys, markSurveyComplete is still used.
  onAccsChainExtend?: () => void;
  setSubmitText: (val: string) => void;
  submitVerbiage: {
    more: { en: string; es: string };
    done: { en: string; es: string };
  };
  validationCode: string | number;
  language?: string;
  surveyIds?: string[];
  ccsFollowups?: string[];
  customCCSSurveyId?: string;
  currentSurveyId?: string;
  updateRequest?: (
    data: {
      prMap: { [p: string]: string };
      steps: RequestStep[];
      language: 'en' | 'es';
      gender: Genders;
      age: number;
      id: string;
    },
    callback?: () => void
  ) => void;
  complaints?: any[];
}) => {
  if (isSubmitting) {
    return;
  }

  setIsSubmitting(true);

  const bodyPickerQuestions = questions.filter(
    question =>
      (question.id === CCSBodyPickerQuestionIds.primary ||
        question.id === CCSBodyPickerQuestionIds.secondary) &&
      question.answer?.value?.parts
  );

  const bodyPartsPicked = bodyPickerQuestions.reduce(
    (obj: { [key: string]: boolean }, question) => {
      Object.keys(question.answer?.value?.parts).forEach((frontOrBack: string) => {
        Object.keys(question.answer?.value?.parts[frontOrBack])?.forEach((part: string) => {
          if (
            question.answer?.value?.parts?.[frontOrBack][part].right ||
            question.answer?.value?.parts?.[frontOrBack][part].left
          ) {
            obj[part] = true;
          }
        });
      });

      return obj;
    },
    {}
  );

  const followUpSurveyIdsRaw: string[] = !complaints
    ? getCCSFollowups(bodyPartsPicked)
    : bodyPickerQuestions.length
      ? Array.from(
          new Set([...getCCSFollowups(bodyPartsPicked), ...getComplaintsCCSFollowups(complaints)])
        )
      : getComplaintsCCSFollowups(complaints);

  const followUpSurveyIds: string[] = followUpSurveyIdsRaw.filter(id => {
    if (
      id === theSurveyIds.ndi &&
      followUpSurveyIdsRaw.includes(theSurveyIds.ndi) &&
      followUpSurveyIdsRaw.includes(theSurveyIds.dash)
    ) {
      return false;
    } else {
      return !(
        id === theSurveyIds.lefs &&
        followUpSurveyIdsRaw.includes(theSurveyIds.oswestry) &&
        followUpSurveyIdsRaw.includes(theSurveyIds.lefs)
      );
    }
  });

  let ccsFollowupSurveyIds: string[] =
    followUpSurveyIds?.filter((match: string) => ccsFollowups?.includes(match)) || [];

  if (surveyIds) {
    ccsFollowupSurveyIds = ccsFollowupSurveyIds.filter(id => !surveyIds.includes(id));
  }

  if (ccsFollowupSurveyIds.length) {
    setSubmitText(submitVerbiage.more[language === 'es' ? 'es' : 'en']);
  }

  // Abbreviated CCS chain. Run the chain first (ACCS #1 → #2 → #3, capped at 3),
  // then queue body-region followups deduped across the entire chain.
  // Why: the patient picks different body regions per ACCS; running followups
  // mid-chain (the prior behavior) interleaved a disability survey between the
  // ACCS instances, which is not what users expect.
  // How: on Yes within the cap, queue only the next ACCS template (no body-region
  // followups yet). On chain end (No or cap), send no followups from the FE — the
  // backend reads all completed ACCS in this request and derives the union of
  // body-region followups across the chain (necessary because the FE doesn't see
  // prior ACCS body picker answers).
  const isAbbreviatedCCSSurvey = questions.some(
    q => q.id === abbreviatedCCSQuestionsIds.moreComplaintsQuestionId
  );
  const answeredYesOnAbbreviatedSurvey = questions.some(
    q => q.id === abbreviatedCCSQuestionsIds.moreComplaintsQuestionId && q.answer?.value === 'Yes'
  );

  // Tracks whether the FE intends to extend the ACCS chain (queue another ACCS
  // instance). Used below to skip the Survey Complete intermediate so the
  // patient transitions straight from the Yes answer to the next ACCS intro —
  // the chain reads as one continuous flow instead of N "Thank you" screens.
  let isExtendingAccsChain = false;

  if (isAbbreviatedCCSSurvey) {
    if (answeredYesOnAbbreviatedSurvey) {
      const accsTemplateId = currentSurveyId || theSurveyIds.abbreviatedCCS;
      const accsCount = (surveyIds || []).filter(id => id === accsTemplateId).length;
      const willExtend = accsCount < 3;

      ccsFollowupSurveyIds = willExtend ? [accsTemplateId] : [];
      isExtendingAccsChain = willExtend;
    } else {
      ccsFollowupSurveyIds = [];
    }
  }

  const answeredYesOnCustomCCS = questions.some(
    q => q.id === customCCSMoreComplaintsQuestionId && q.answer?.value === 'Yes'
  );

  if (answeredYesOnCustomCCS && customCCSSurveyId) {
    ccsFollowupSurveyIds = [customCCSSurveyId];
  }

  const res = await submitSurvey(
    questions,
    kiosk,
    language === 'es' ? spanishQuestions : undefined,
    ccsFollowupSurveyIds,
    validationCode
  );

  // `updateRequest` is the legacy public-request-flow chain handler:
  // when CCS produces follow-up surveys, the caller supplies a function
  // that loads them into the in-flight request UI. The patient portal
  // (healthcom-schedule SurveyRunner) doesn't chain followups inline —
  // the backend creates new PatientSurvey rows the patient picks up
  // from the home tasks list — so it doesn't pass `updateRequest`. If
  // we used `updateRequest?.(...)` and returned, the optional chain
  // would silently no-op, leaving the UI parked on the just-answered
  // question with no transition. Falling through to
  // `markSurveyComplete` + `complete` lets the portal navigate home.
  //
  // For ACCS submits the backend may queue chain-end followups even
  // when the FE sent an empty list, so we trigger refresh on any ACCS
  // submit, not just when `ccsFollowupSurveyIds.length > 0`.
  const expectsRequestRefresh = ccsFollowupSurveyIds.length > 0 || isAbbreviatedCCSSurvey;

  if (expectsRequestRefresh && updateRequest) {
    setIsSubmitting(false);

    // Chain-extending ACCS submits skip the Survey Complete "Thank you" screen
    // entirely so the patient transitions straight from the Yes answer to the
    // next ACCS Get Started. Chain end / non-ACCS still uses markSurveyComplete.
    const refreshCallback =
      isExtendingAccsChain && onAccsChainExtend
        ? () => onAccsChainExtend()
        : () => markSurveyComplete();

    return updateRequest(
      res as {
        steps: RequestStep[];
        language: 'en' | 'es';
        gender: Genders;
        age: number;
        id: string;
        prMap: { [key: string]: string };
      },
      refreshCallback
    );
  }

  if (markSurveyComplete) {
    markSurveyComplete();
  }

  setIsSubmitting(false);

  return complete();
};

export const bodyPickerSubmit = async (
  parts: BodyParts,
  sourceOfPain: SourceOfPain,
  spanishQuestions: SurveyQuestion[],
  questions: SurveyQuestion[],
  kiosk: boolean,
  language: string,
  currentQuestion: SurveyQuestion,
  setIsSubmitting: (val: boolean) => void,
  isSubmitting: boolean,
  submitSurvey: (
    questions: SurveyQuestion[],
    kiosk: boolean,
    spanishQuestions?: SurveyQuestion[],
    ccsFollowupSurveyIds?: string[],
    validationCode?: string | number
  ) => Promise<any>,
  complete: () => void,
  markSurveyComplete: () => void,
  setSubmitText: (val: string) => void,
  submitVerbiage: {
    more: { en: string; es: string };
    done: { en: string; es: string };
  },
  setSpanishQuestions: React.Dispatch<React.SetStateAction<SurveyQuestion[]>>,
  setQuestions: React.Dispatch<React.SetStateAction<SurveyQuestion[]>>,
  setCurrentQuestion: (val: SurveyQuestion) => void,
  validationCode: string | number,
  isProvider = false,
  additionalComplaint = false,
  aaWI?: string,
  surveyIds?: string[],
  ccsFollowups?: string[],
  updateRequest?: (
    data: {
      prMap: { [key: string]: string };
      steps: RequestStep[];
      language: 'en' | 'es';
      gender: Genders;
      age: number;
      id: string;
    },
    callback?: () => void
  ) => void,
  startingProblemArea?: number,
  abbreviatedCCS = false,
  remainingSteps?: number[],
  customQuestions?: SurveyQuestion[],
  allowAdditionalComplaints = false,
  saveDraft?: (
    questions: SurveyQuestion[],
    spanishQuestions: SurveyQuestion[],
    force?: boolean
  ) => Promise<void>,
  age?: number,
  gender?: Genders
) => {
  const newQuestions = [...questions];
  const newSpanishQuestions = spanishQuestions ? [...spanishQuestions] : [];
  const currentQuestionIndex = newQuestions.findIndex(q => q.id === currentQuestion.id);

  if (newQuestions[currentQuestionIndex]) {
    newQuestions[currentQuestionIndex].answer = {
      type: 'bodypicker',
      value: {
        parts,
        sourceOfPain,
      },
    };
  }

  setQuestions(newQuestions);

  if (setSpanishQuestions && language === 'es') {
    if (newSpanishQuestions[currentQuestionIndex]) {
      newSpanishQuestions[currentQuestionIndex].answer = {
        type: 'bodypicker',
        value: {
          parts,
          sourceOfPain,
        },
      };
    }

    setSpanishQuestions(newSpanishQuestions);
  }

  // If this is the third bodypicker, don't ask questions
  if (currentQuestion.id === CCSBodyPickerQuestionIds.tertiary) {
    await saveDraft?.(newQuestions, newSpanishQuestions, true);

    return submitStuff({
      questions: newQuestions,
      kiosk,
      spanishQuestions: newSpanishQuestions,
      setIsSubmitting,
      isSubmitting,
      submitSurvey,
      complete,
      markSurveyComplete,
      setSubmitText,
      submitVerbiage,
      validationCode,
      language,
      surveyIds,
      ccsFollowups,
      updateRequest,
    });
  }

  if (customQuestions?.length) {
    return addCustomCCSQuestions(
      parts,
      language,
      currentQuestion,
      questions,
      spanishQuestions,
      customQuestions,
      setCurrentQuestion,
      setQuestions,
      setSpanishQuestions,
      startingProblemArea,
      allowAdditionalComplaints,
      abbreviatedCCS,
      age,
      gender
    );
  }

  return addChiefComplaintQuestions(
    parts,
    language,
    currentQuestion,
    questions,
    spanishQuestions,
    setCurrentQuestion,
    setQuestions,
    setSpanishQuestions,
    additionalComplaint,
    isProvider,
    aaWI,
    startingProblemArea,
    abbreviatedCCS,
    allowAdditionalComplaints,
    age,
    gender
  );
};

const getSurveyQuestion = (questions: SurveyQuestion[], id: string): SurveyQuestion => {
  return questions.find(q => q.id === id) || questions[0];
};

export const getNextQuestionIndex = ({
  questions,
  age,
  gender,
}: {
  questions: SurveyQuestion[];
  age?: number;
  gender?: Genders;
}): number => {
  return questions.findIndex(q => {
    // Already answered...
    if (q.answer) {
      return false;
    }

    if (!q.ifSome?.length && !q.if?.length) {
      return true;
    }

    // If there are conditions, check them and return true if all are met...
    if (q.ifSome?.length) {
      return q.ifSome.some((qif: any) => {
        if (qif.id) {
          if (qif.type === SurveyQuestionIfType.equal) {
            const dependentQ = getSurveyQuestion(questions, qif.id);
            const dependentA = dependentQ.answer?.value;

            if (Array.isArray(dependentA)) {
              return (
                dependentA &&
                dependentA.some(qv =>
                  Array.isArray(qif.value) ? qif.value.includes(qv) : qif.value === qv
                )
              );
            } else if (typeof dependentA === 'string' || typeof dependentA === 'number') {
              return dependentA && Array.isArray(qif.value)
                ? qif.value.includes(dependentA)
                : qif.value === dependentA;
            }
          } else if (qif.type === SurveyQuestionIfType.greaterThan) {
            const dependentQ = getSurveyQuestion(questions, qif.id);
            const dependentA = dependentQ.answer?.value;

            return dependentA > qif.value;
          } else if (qif.type === SurveyQuestionIfType.lessThan) {
            const dependentQ = getSurveyQuestion(questions, qif.id);
            const dependentA = dependentQ.answer?.value;

            return dependentA < qif.value;
          } else if (qif.type === SurveyQuestionIfType.notAnswered) {
            const dependentQ = getSurveyQuestion(questions, qif.id);
            const dependentA = dependentQ.answer?.value;

            return !dependentA;
          }
        }

        return false;
      });
    }

    return q.if?.every((qif: any) => {
      if (!qif) {
        // Somehow it is undefined...this definitely happens for the same incident questions
        return true;
      }

      if (qif?.demographic) {
        if (qif.property === 'gender') {
          if (!gender) {
            return true;
          }

          return Array.isArray(qif.value) ? qif.value.includes(gender) : qif.value === gender;
        } else if (qif.property === 'age') {
          if (!age) {
            return true;
          }

          if (qif.type === SurveyQuestionIfType.equal) {
            return age === qif.value;
          } else if (qif.type === SurveyQuestionIfType.greaterThan) {
            return age > qif.value;
          } else if (qif.type === SurveyQuestionIfType.lessThan) {
            return age < qif.value;
          }
        }
      } else if (qif?.type === SurveyQuestionIfType.equal) {
        const dependentQ = getSurveyQuestion(questions, qif.id);
        const dependentA = dependentQ.answer?.value;

        if (Array.isArray(dependentA)) {
          return (
            dependentA &&
            dependentA.some(qv =>
              Array.isArray(qif.value) ? qif.value.includes(qv) : qif.value === qv
            )
          );
        } else if (typeof dependentA === 'string' || typeof dependentA === 'number') {
          return dependentA && Array.isArray(qif.value)
            ? qif.value.includes(dependentA)
            : qif.value === dependentA;
        }
      } else if (qif?.type === SurveyQuestionIfType.greaterThan) {
        const dependentQ = getSurveyQuestion(questions, qif.id);
        const dependentA = dependentQ.answer?.value;

        return dependentA > qif.value;
      } else if (qif?.type === SurveyQuestionIfType.lessThan) {
        const dependentQ = getSurveyQuestion(questions, qif.id);
        const dependentA = dependentQ.answer?.value;

        return dependentA < qif.value;
      } else if (qif.type === SurveyQuestionIfType.notAnswered) {
        const dependentQ = getSurveyQuestion(questions, qif.id);
        const dependentA = dependentQ.answer?.value;

        return !dependentA;
      }

      return false;
    });
  });
};

// Thin wrapper around the shared rule helper. The actual body-region rules
// live in libs/core/functions/deriveCCSFollowups.ts.
const getCCSFollowups = (bodyPartsPicked: { [key: string]: boolean }) =>
  deriveCCSFollowupsFromBodyParts(bodyPartsPicked);

const getComplaintsCCSFollowups = (complaints: any[]) => {
  return complaints
    ?.map((c: any) => c.complaint)
    ?.reduce((arr: string[], rawPart: any) => {
      const part = rawPart.replace(/-/g, ' ').toLowerCase();

      if (part === 'neck' && !arr.includes(theSurveyIds.ndi)) {
        arr.push(theSurveyIds.ndi);
      }

      if (
        (part === 'upper back' || part === 'upper lower back') &&
        !arr.includes(theSurveyIds.oswestry) &&
        !complaints.some((c: any) => c.complaint.replace(/-/g, ' ').toLowerCase() === 'neck')
      ) {
        arr.push(theSurveyIds.oswestry);
      }

      if (part === 'lower back' && !arr.includes(theSurveyIds.oswestry)) {
        arr.push(theSurveyIds.oswestry);
      }

      if (
        (part === 'shoulder' ||
          part === 'elbow' ||
          part === 'hand' ||
          part === 'wrist' ||
          part === 'arm') &&
        !arr.includes(theSurveyIds.dash) &&
        !complaints.some((c: any) => c.complaint.replace(/-/g, ' ').toLowerCase() === 'neck')
      ) {
        arr.push(theSurveyIds.dash);
      }

      if (
        (part === 'hip' ||
          part === 'knee' ||
          part === 'thigh' ||
          part === 'foot' ||
          part === 'ankle' ||
          part === 'leg') &&
        !arr.includes(theSurveyIds.lefs) &&
        !complaints
          ?.map((c: any) => c.complaint)
          ?.some((c: string) =>
            ['lower back', 'upper back', 'uppper lower back'].includes(
              c.replace(/-/g, ' ').toLowerCase()
            )
          )
      ) {
        arr.push(theSurveyIds.lefs);
      }

      return arr;
    }, []);
};
